import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Updates only location labels and lightbox metadata after asynchronous reverse
// geocoding. Matching and card rendering never wait for this controller or the
// geocoding provider. A one-shot reconciliation closes gaps from suspended devices,
// Action Cable reconnects, and cached Turbo page restoration.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this._locations = {}
    this._onRestore = () => this.reconcile()
    this._onVisibility = () => {
      if (document.visibilityState === "visible") this.reconcile()
    }

    document.addEventListener("native:restore", this._onRestore)
    document.addEventListener("visibilitychange", this._onVisibility)

    this._subscription = consumer.subscriptions.create(
      { channel: "MatchingChannel" },
      {
        connected: () => this.reconcile(),
        received: (data) => {
          if (data?.type !== "location_updated") return
          this.applyLocation(data.discovery_id, data.location)
        }
      }
    )

    // Feed inserts, infinite-scroll pages, and photo-cell replacements can add new
    // location-bearing nodes after connect. Reapply known names and reconcile only
    // newly-visible unresolved discoveries.
    this._observer = new MutationObserver(() => {
      clearTimeout(this._mutationTimer)
      this._mutationTimer = setTimeout(() => {
        this.applyKnownLocations()
        this.reconcile()
      }, 50)
    })
    this._observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    document.removeEventListener("native:restore", this._onRestore)
    document.removeEventListener("visibilitychange", this._onVisibility)
    this._subscription?.unsubscribe()
    this._observer?.disconnect()
    clearTimeout(this._mutationTimer)
  }

  async reconcile() {
    if (!this.hasUrlValue) return
    if (this._reconciling) {
      this._reconcileAgain = true
      return
    }

    const ids = this.pendingIds()
    if (!ids.length) return

    this._reconciling = true
    try {
      for (let offset = 0; offset < ids.length; offset += 100) {
        const url = new URL(this.urlValue, window.location.origin)
        url.searchParams.set("ids", ids.slice(offset, offset + 100).join(","))

        const response = await fetch(url, {
          headers: { "Accept": "application/json" },
          credentials: "same-origin"
        })
        if (!response.ok) throw new Error(`Location reconciliation failed (${response.status})`)

        const data = await response.json()
        Object.entries(data.locations || {}).forEach(([id, location]) => {
          this.applyLocation(id, location)
        })
      }
    } catch (error) {
      console.warn("[location-updates] reconciliation failed", error)
    } finally {
      this._reconciling = false
      if (this._reconcileAgain) {
        this._reconcileAgain = false
        this.reconcile()
      }
    }
  }

  pendingIds() {
    return Array.from(this.element.querySelectorAll("[data-location-pending='true'][data-location-id]"))
      .map((element) => element.dataset.locationId)
      .filter((id, index, ids) => id && ids.indexOf(id) === index)
  }

  applyLocation(rawId, location) {
    const id = String(parseInt(rawId, 10))
    if (!location || id === "NaN") return

    this._locations[id] = location

    this.element.querySelectorAll(`[data-location-id="${id}"]`).forEach((element) => {
      if (element.textContent !== location) element.textContent = location
      element.dataset.locationPending = "false"
    })

    this.element.querySelectorAll(`[data-location-meta-id="${id}"]`).forEach((element) => {
      if (element.dataset.lightboxMeta !== location) element.dataset.lightboxMeta = location
    })
  }

  applyKnownLocations() {
    Object.entries(this._locations).forEach(([id, location]) => this.applyLocation(id, location))
  }
}
